<template>
  <component
    :is="type"
    class="list"
    :class="[
      {
        'list--unstyled': unstyled,
        'list--horizontal': horizontal || horizontalNarrow,
        'list--horizontal-narrow': horizontalNarrow,
        'list--icons': icons,
        'list--ordered': ordered,
        'list--separator': separator,
        'list--colored': colored,
        'list--columns': columns,
        'list--spaced': spaced,
        'list--steps': steps,
        'list--green-bullets': greenBullets,
        'list--connected-icons': connectedIcons,
        'list--small-bullets': smallBullets,
      },
      backgroundColor ? 'list--bg-' + backgroundColor : '',
      foregroundColor ? 'list--fg-' + foregroundColor : '',
    ]"
  >
    <slot />
  </component>
</template>

<script>
export default {
  props: {
    ordered: {
      type: Boolean,
    },
    unstyled: {
      type: Boolean,
    },
    horizontal: {
      type: Boolean,
    },
    horizontalNarrow: {
      type: Boolean,
    },
    separator: {
      type: Boolean,
    },
    icons: {
      type: Boolean,
    },
    backgroundColor: {
      type: String,
    },
    foregroundColor: {
      type: String,
    },
    colored: {
      type: Boolean,
    },
    spaced: {
      type: Boolean,
    },
    columns: {
      type: Boolean,
    },
    steps: {
      type: Boolean,
    },
    greenBullets: {
      type: Boolean,
    },
    connectedIcons: {
      type: Boolean,
    },
    smallBullets: {
      type: Boolean,
    },
  },
  computed: {
    type() {
      if (this.ordered) {
        return 'ol';
      }
      return 'ul';
    },
  },
};
</script>

<style lang="scss" scoped>
.list {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin-bottom: $pad;

  &:last-child {
    margin-bottom: 0;
  }

  // Colors
  @each $palette, $tones in $palettes {
    @each $tone, $hex in $tones {
      @if $tone == 'base' {
        &.list--bg-#{$palette} {
          background-color: $hex;
        }
        &.list--fg-#{$palette} {
          color: $hex;
        }
      } @else {
        &.list--bg-#{$palette}-#{$tone} {
          background-color: $hex;
        }
        &.list--fg-#{$palette}-#{$tone} {
          color: $hex;
        }
      }
    }
  }
}

.list--unstyled {
  padding: 0;
  margin: 0 0 $pad 0;
  list-style: none;
}

.list--horizontal {
  flex-direction: row;
  flex-wrap: wrap;

  :slotted(li) {
    margin-right: 35px;

    &:last-child {
      margin-right: 0;
    }
  }
}

.list--horizontal-narrow {
  :slotted(li) {
    margin-right: 10px;

    &:last-child {
      margin-right: 0;
    }
  }
}

.list--icons {
  :slotted(li) {
    position: relative;
    padding-left: $pad * 2;

    .icon {
      position: absolute;
      left: 0;
      top: 0;
    }
  }
}

.list--separator {
  :slotted(li) {
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 1px;
      height: 60%;
      right: -17.5px;
      top: 0;
      bottom: 0;
      margin: auto 0;
      background-color: palette(black);
    }

    &:last-child {
      &:after {
        display: none;
      }
    }
  }
}

.list--colored {
  border-radius: map-get($border-radiuses, 'l');

  :slotted(li) {
    padding: ($pad * 0.5) ($pad * 0.5) ($pad * 0.5) $pad * 3;
    border-bottom: 1px solid $color-white;

    &:last-child {
      border-bottom: 0;
    }
  }

  &.list--icons {
    :deep(.icon) {
      left: $pad;
      bottom: 0;
      display: flex;
      align-items: flex-start;
      padding-top: $pad * 0.5;
    }
  }
}

.list--columns {
  @include bp(m) {
    column-count: 2;
    display: block;
  }
}

.list--spaced {
  :slotted(li) {
    margin-bottom: $pad * 0.5;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.list--steps {
  :slotted(li) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    border-bottom: 1px solid palette(sand);
    padding-bottom: $pad;
    margin-bottom: $pad;

    &:last-child {
      border-bottom: none;
    }

    span {
      font-size: map-get($font-sizes, 'xxl');
      color: palette(grey, light);
      flex: 0 0 30px;
      display: block;
      font-family: $font-primary;
    }
  }
}

.list--register {
  :slotted(li) {
    line-height: $space;
    margin-top: math.div($space, 4);
    @include bp(s) {
      line-height: 1.5 * $space;
      margin-top: 0;
    }

    &:first-of-type {
      margin-top: 0;
    }
  }
}

.list--green-bullets {
  list-style-type: none;

  :slotted(li) {
    &::before {
      display: inline-block;
      content: ' ';
      background: palette(green, darker);
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-right: 8px;
      position: relative;
      top: -1px;
    }
  }
}

.list--connected-icons {
  :slotted(li) {
    margin-bottom: $pad * 2;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    position: relative;

    .icon {
      margin-right: $pad;
      line-height: 1;

      &:after {
        content: ' ';
        display: block;
        width: 1px;
        position: absolute;
        left: 50%;
        top: 100%;
        transform: translateX(-50%);
        height: 90%;
        border-right: dashed $color-general-border-neutral-default 1px;
      }
    }

    &:last-child .icon:after {
      display: none;
    }
  }
}

.list--small-bullets {
  list-style-type: none;
  padding: 0;
  line-height: map-get($font-line-heights, 's');

  :slotted(li) {
    position: relative;
    padding-left: ($space * 0.5);

    &::before {
      display: inline-block;
      content: ' ';
      background: palette(grey, dark);
      width: 3px;
      height: 3px;
      border-radius: map-get($border-radiuses, 'half');
      position: absolute;
      left: 0;
      top: math.div(map-get($font-line-heights, 's'), 2);
      transform: translateY(-50%);
    }
  }
}
</style>
